import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import AuthContext from '../context/AuthContext';

const API_BASE = process.env.REACT_APP_API_URL || '';

function resolveImageUrl(url) {
  if (!url || typeof url !== 'string' || url.trim() === '') return '/placeholder-image.png';
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('blob:')) return url;
  const path = url.startsWith('/') ? url : `/${url}`;
  return `${API_BASE}${path}`;
}

const EditProduct = () => {
  const { branch, id } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useContext(AuthContext);
  const [name, setName] = useState('');
  const [itemCode, setItemCode] = useState('');
  const [quantity, setQuantity] = useState(0);
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchProduct();
    // eslint-disable-next-line
  }, [id, branch]);

  const fetchProduct = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/products/${branch}/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setName(response.data.product.name);
        setItemCode(response.data.product.item_code);
        setQuantity(response.data.product.quantity);
        setImagePreview(resolveImageUrl(response.data.product.image_url));
      } else {
        setError('Failed to fetch product details');
      }
    } catch (error) {
      setError('Failed to fetch product details');
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImage(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || !itemCode) {
      setError('Name and item code are required');
      return;
    }
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('name', name);
      formData.append('itemCode', itemCode);
      formData.append('quantity', quantity);
      if (image) {
        formData.append('image', image);
      }
      const response = await axios.put(`/api/products/${branch}/${id}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        alert('Product updated successfully');
        navigate(`/${branch}`);
      } else {
        setError(response.data.message || 'Failed to update product');
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to update product');
    } finally {
      setLoading(false);
    }
  };

  if (!isAdmin()) {
    navigate('/');
    return null;
  }

  if (loading) return <div>Loading...</div>;

  return (
    <div className="row justify-content-center">
      <div className="col-md-6 col-lg-4">
        <div className="card shadow">
          <div className="card-body">
            <h3 className="mb-4">Edit Product</h3>
            {error && <div className="alert alert-danger">{error}</div>}
            <form onSubmit={handleSubmit} encType="multipart/form-data">
              <div className="mb-3">
                <label htmlFor="name" className="form-label">Name</label>
                <input type="text" className="form-control" id="name" value={name} onChange={e => setName(e.target.value)} required />
              </div>
              <div className="mb-3">
                <label htmlFor="itemCode" className="form-label">Item Code</label>
                <input type="text" className="form-control" id="itemCode" value={itemCode} onChange={e => setItemCode(e.target.value)} required />
              </div>
              <div className="mb-3">
                <label htmlFor="quantity" className="form-label">Quantity</label>
                <input type="number" className="form-control" id="quantity" value={quantity} onChange={e => setQuantity(Number(e.target.value))} min="0" />
              </div>
              <div className="mb-3">
                <label htmlFor="image" className="form-label">Product Image</label>
                <input type="file" className="form-control" id="image" accept="image/*" onChange={handleImageChange} />
                {imagePreview && (
                  <div className="mt-2 text-center">
                    <img 
                      src={imagePreview} 
                      alt="Preview" 
                      className="img-thumbnail" 
                      style={{ maxHeight: '200px' }} 
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = '/placeholder-image.png';
                      }}
                    />
                  </div>
                )}
              </div>
              <div className="d-grid">
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? 'Updating...' : 'Update Product'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditProduct;
